import React, { useEffect, useMemo, useRef, useState } from "react";

// Santa’s Workshop – Personalized Letter Generator (React + Tailwind)
// Full component with custom confirmation modal (no ESLint no-restricted-globals)
// - Live preview updates as you type
// - Saves progress to localStorage
// - Copy letter text to clipboard
// - Print/Save as PDF via browser print

export default function SantasWorkshopLetter() {
  const STORAGE_KEY = "santas-workshop-letter-v1";
  const [form, setForm] = useState({
    childName: "[Child’s First Name]",
    age: "6",
    hometown: "[Hometown]",
    accomplishments: "being kind, helping at home, and trying your best at school",
    favoriteTreats: "cookies for Santa and carrots for the reindeer",
    customGiftHint: "I might have a sparkly surprise on my sleigh just for you!",
    parentNote: "(Optional) We are so proud of you! Love, Mom & Dad",
    date: new Date().toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    includeParentNote: true,
    includeRudolphLine: true,
  });

  const [showConfirm, setShowConfirm] = useState(false);

  // Load/save to localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setForm(JSON.parse(saved));
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch {}
  }, [form]);

  const update = (k: keyof typeof form, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const letterLines = useMemo(() => {
    return {
      header: `Ho Ho Ho!`,
      address: `North Pole`,
      date: form.date,
      salutation: `Dear ${form.childName},`,
      p1: `Merry Christmas! I just checked my Nice List, and guess what? Your name is shining bright in golden letters! Way to go, ${form.childName}!`,
      p2: `Mrs. Claus and I are so proud of you for ${form.accomplishments}. That makes me happier than a jingling sleigh bell in fresh snow!`,
      p3: `Here at Santa’s Workshop, the elves are wrapping, the hammers are tapping, and the giggles are nonstop. ${
        form.includeRudolphLine ? "Rudolph’s nose is glowing extra bright to light the way tonight!" : "The reindeer are doing their flying warm‑ups right now!"
      } ${form.customGiftHint}`,
      p4: `On Christmas Eve, remember to tuck in early. If you’d like, you can leave ${form.favoriteTreats}. The elves say that ${form.childName} from ${form.hometown} has the most thoughtful treats!`,
      closing: `Keep being kind, curious, and brave — the world shines brighter because of you.`,
      signoff: `With love and Christmas cheer,`,
      signature: `🎅 Santa Claus`,
      footer: `Santa’s Workshop • The North Pole`,
      ps: `P.S. The elves wanted me to tell you that you make them smile every time they talk about you!`,
      parentNote: form.parentNote,
    };
  }, [form]);

  const letterPlainText = useMemo(() => {
    const l = letterLines;
    const parts = [
      `${l.header}`,
      `${l.address}`,
      `${l.date}`,
      "",
      `${l.salutation}`,
      "",
      `${l.p1}`,
      `${l.p2}`,
      `${l.p3}`,
      `${l.p4}`,
      "",
      `${l.closing}`,
      `${l.signoff}`,
      `${l.signature}`,
      `${l.footer}`,
      "",
      `${l.ps}`,
      form.includeParentNote ? `\nFamily Note: ${l.parentNote}` : "",
    ].filter(Boolean);
    return parts.join("\n");
  }, [letterLines, form.includeParentNote]);

  const previewRef = useRef<HTMLDivElement>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(letterPlainText);
      alert("Letter copied to clipboard!");
    } catch (e) {
      alert("Could not copy. Please select the text and copy manually.");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Modal-driven reset to avoid confirm()
  const handleResetClick = () => setShowConfirm(true);
  const handleResetConfirm = () => {
    setShowConfirm(false);
    setForm({
      childName: "[Child’s First Name]",
      age: "6",
      hometown: "[Hometown]",
      accomplishments: "being kind, helping at home, and trying your best at school",
      favoriteTreats: "cookies for Santa and carrots for the reindeer",
      customGiftHint: "I might have a sparkly surprise on my sleigh just for you!",
      parentNote: "(Optional) We are so proud of you! Love, Mom & Dad",
      date: new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      includeParentNote: true,
      includeRudolphLine: true,
    });
  };

  return (
    <div className="min-h-screen bg-red-50">
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-area { box-shadow: none !important; background: white !important; }
          body { background: white; }
        }
      `}</style>

      <header className="no-print border-b border-red-200 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-6xl px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-extrabold text-red-700 tracking-tight">🎄 Santa’s Workshop – Letter from Santa</h1>
          <div className="flex gap-2">
            <button onClick={handleCopy} className="px-3 py-2 rounded-2xl bg-red-600 text-white hover:bg-red-700 shadow">Copy Text</button>
            <button onClick={handlePrint} className="px-3 py-2 rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 shadow">Print / Save PDF</button>
            <button onClick={handleResetClick} className="px-3 py-2 rounded-2xl bg-gray-200 hover:bg-gray-300 shadow">Reset</button>
          </div>
        </div>
      </header>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-xl max-w-sm w-full text-center">
            <h3 className="text-lg font-semibold mb-2">Reset all fields?</h3>
            <p className="text-sm text-gray-600 mb-4">This will restore the default text for the letter.</p>
            <div className="flex justify-center gap-3">
              <button onClick={handleResetConfirm} className="bg-red-600 text-white px-4 py-2 rounded-2xl hover:bg-red-700">Yes, Reset</button>
              <button onClick={() => setShowConfirm(false)} className="bg-gray-200 px-4 py-2 rounded-2xl hover:bg-gray-300">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-6xl p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Panel */}
        <section className="no-print bg-white rounded-2xl shadow p-4 lg:p-6 border border-red-100">
          <h2 className="text-lg font-semibold text-red-700 mb-4">Personalize the Letter</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LabeledInput label="Child’s first name" value={form.childName} onChange={(v) => update("childName", v)} placeholder="e.g., Emma" />
            <LabeledInput label="Age" value={form.age} onChange={(v) => update("age", v)} placeholder="6" />
            <LabeledInput label="Hometown" value={form.hometown} onChange={(v) => update("hometown", v)} placeholder="e.g., Boulder, CO" />
            <LabeledInput label="Favorite treats left out" value={form.favoriteTreats} onChange={(v) => update("favoriteTreats", v)} placeholder="cookies and carrots" />
            <LabeledTextArea label="Proud of (accomplishments)" value={form.accomplishments} onChange={(v) => update("accomplishments", v)} placeholder="being kind, helping at home, trying your best" rows={3} />
            <LabeledTextArea label="Gift hint (optional, keep vague!)" value={form.customGiftHint} onChange={(v) => update("customGiftHint", v)} rows={3} />
            <LabeledInput label="Letter date" value={form.date} onChange={(v) => update("date", v)} />
            <div className="flex items-center gap-2 mt-2">
              <input id="rudolph" type="checkbox" className="h-4 w-4" checked={form.includeRudolphLine} onChange={(e) => update("includeRudolphLine", e.target.checked)} />
              <label htmlFor="rudolph" className="text-sm text-gray-700">Include the Rudolph line</label>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input id="parentNoteToggle" type="checkbox" className="h-4 w-4" checked={form.includeParentNote} onChange={(e) => update("includeParentNote", e.target.checked)} />
              <label htmlFor="parentNoteToggle" className="text-sm text-gray-700">Include a short family note</label>
            </div>
            {form.includeParentNote && (
              <div className="md:col-span-2">
                <LabeledTextArea label="Family note (printed at bottom)" value={form.parentNote} onChange={(v) => update("parentNote", v)} rows={2} />
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-4">Tip: Keep gift hints magical but not specific, so surprises stay safe!</p>
        </section>

        {/* Letter Preview */}
        <section ref={previewRef} className="print-area bg-white rounded-2xl shadow p-6 border-2 border-red-200">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 mb-2">
              <span className="text-3xl">🎅</span>
              <h2 className="text-2xl font-extrabold text-red-700">Letter from Santa Claus</h2>
              <span className="text-3xl">🎄</span>
            </div>
            <div className="text-sm text-gray-600">Santa’s Workshop • The North Pole</div>
          </div>

          <div className="mt-4 text-sm text-gray-700">
            <div className="text-right">
              <div>North Pole</div>
              <div>{form.date}</div>
            </div>

            <p className="mt-6">Dear <span className="font-semibold">{form.childName}</span>,</p>

            <p className="mt-3">Merry Christmas! I just checked my Nice List, and guess what? Your name is shining bright in golden letters! Way to go, {form.childName}!</p>

            <p className="mt-3">Mrs. Claus and I are so proud of you for {form.accomplishments}. That makes me happier than a jingling sleigh bell in fresh snow!</p>

            <p className="mt-3">Here at Santa’s Workshop, the elves are wrapping, the hammers are tapping, and the giggles are nonstop. {form.includeRudolphLine ? "Rudolph’s nose is glowing extra bright to light the way tonight!" : "The reindeer are doing their flying warm‑ups right now!"} {form.customGiftHint}</p>

            <p className="mt-3">On Christmas Eve, remember to tuck in early. If you’d like, you can leave {form.favoriteTreats}. The elves say that {form.childName} from {form.hometown} has the most thoughtful treats!</p>

            <p className="mt-3">Keep being kind, curious, and brave — the world shines brighter because of you.</p>

            <p className="mt-6">With love and Christmas cheer,</p>
            <p className="mt-2 font-semibold">🎅 Santa Claus</p>
            <p className="text-xs text-gray-500">Santa’s Workshop – The North Pole</p>

            <p className="mt-4 italic">P.S. The elves wanted me to tell you that you make them smile every time they talk about you!</p>

            {form.includeParentNote && (
              <div className="mt-6 p-3 rounded-xl bg-red-50 border border-red-100">
                <div className="text-xs font-semibold text-red-700">Family Note</div>
                <div className="text-sm">{form.parentNote}</div>
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="no-print mx-auto max-w-6xl p-4 text-center text-xs text-gray-500">
        © {new Date().getFullYear()} Santa’s Workshop • Made with ❤️ and hot cocoa
      </footer>
    </div>
  );
}

function LabeledInput({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <input
        className="mt-1 w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-red-300"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function LabeledTextArea({ label, value, onChange, rows = 4, placeholder }: { label: string; value: string; onChange: (v: string) => void; rows?: number; placeholder?: string }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <textarea
        className="mt-1 w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-red-300"
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
